import { create } from "zustand";
import { useRequestStore, GetAccessTokenFn } from "../BackendStore";
import { SignupRequest, User } from "../../Contracts/User";
import { MultiGetResponse } from "../../Contracts/Common";
import { persist } from "zustand/middleware";
import { useBannerStore } from "../Banner";
import { BannerType } from "../../Contracts/Banner";

const getUsersUrl = "/v1/payments/team/users";
const signupRequestUrl = "/v1/payments/signup";
const getCurrentUserUrl = "/v1/payments/team/currentUser/ ";
const inviteUserUrl = "/v1/payments/team/users/invite/";
const editUserUrl = "/v1/payments/team/users/<userId>";
const deleteUserUrl = "/v1/payments/team/users/<userId>";
const deleteUserInviteUrl = "/v1/payments/team/users/invite/<userId>";
const resendInviteUrl = "/v1/payments/team/users/invite/<userId>/resend/";
const acceptInviteUrl = "/v1/payments/teams/users/invite/<teamInviteId>/accept/";

interface UserState {
  isLoading: boolean;
  users: User[];
  usersCount?: number;
  currentUser?: User;
  getUsers: (
    getAccessTokenSilently: GetAccessTokenFn,
    limit?: number,
    sort?: string,
    page?: number,
    filters?: string
  ) => Promise<void>;
  requestSignup: (signupRequest: SignupRequest) => Promise<boolean>;
  getCurrentUser: (getAccessTokenSilently: GetAccessTokenFn) => Promise<void>;
  resetCurrentUser: () => void;
  inviteNewUser: (getAccessTokenSilently: GetAccessTokenFn, email: string) => Promise<void>;
  editUser: (
    getAccessTokenSilently: GetAccessTokenFn,
    userId: number,
    firstName: string,
    lastName: string
  ) => Promise<void>;
  deleteUser: (getAccessTokenSilently: GetAccessTokenFn, user: User) => Promise<void>;
  resendInvite: (getAccessTokenSilently: GetAccessTokenFn, userId: string) => Promise<void>;
  acceptInvite: (
    team_invite_id: string,
    firstName: string,
    lastName: string,
    token: string
  ) => Promise<boolean>;
}

export const useUserStore = create<UserState>()(
  persist(
    set => {
      const { getRequest, postRequestNoAuth, postRequest, putRequest, deleteRequest } =
        useRequestStore.getState();
      const { addBanner } = useBannerStore.getState();
      const getUsers = async (
        getAccessTokenSilently: GetAccessTokenFn,
        limit?: number,
        sort?: String,
        page?: number,
        filters?: string
      ) => {
        set({ isLoading: true });
        try {
          const response = await getRequest<MultiGetResponse<User>>(
            getAccessTokenSilently,
            getUsersUrl,
            {
              limit,
              sort,
              page,
              include_total_count: true,
              filters
            }
          );
          set({
            users: response.data,
            usersCount: response.filtered_count_map?.total_count,
            isLoading: false
          });
        } catch (error) {
          console.error("Error fetching users", error);
          set({ isLoading: false });
        }
      };
      const requestSignup = async (signupRequest: SignupRequest): Promise<boolean> => {
        set({ isLoading: true });
        try {
          await postRequestNoAuth(signupRequestUrl, signupRequest);
          addBanner({
            type: BannerType.Success,
            message: "Signup request submitted successfully."
          });
          set({ isLoading: false });
          return true;
        } catch (error) {
          console.error("Error signing up user");
          set({ isLoading: false });
          return false;
        }
      };
      const getCurrentUser = async (getAccessTokenSilently: GetAccessTokenFn) => {
        set({ isLoading: true });
        try {
          const response = await getRequest<User>(getAccessTokenSilently, getCurrentUserUrl);
          set({ currentUser: response, isLoading: false });
        } catch (error) {
          console.error("Error fetching current user", error);
          set({ isLoading: false });
        }
      };
      const resetCurrentUser = () => {
        set({ currentUser: undefined });
      };

      const inviteNewUser = async (getAccessTokenSilently: GetAccessTokenFn, email: string) => {
        try {
          const response = await postRequest<{ email: string }, User>(
            getAccessTokenSilently,
            inviteUserUrl,
            { email }
          );
          addBanner({
            type: BannerType.Success,
            message: `User ${email} invited successfully.`
          });
          const users = useUserStore.getState().users;
          const updatedUsers = [...users, response];
          set({ users: updatedUsers, isLoading: false });
        } catch (error) {
          console.error("Error inviting user", error);
          set({ isLoading: false });
        }
      };

      const editUser = async (
        getAccessTokenSilently: GetAccessTokenFn,
        userId: number,
        firstName: string,
        lastName: string
      ) => {
        try {
          const response = await putRequest<{ first_name: string; last_name: string }, User>(
            getAccessTokenSilently,
            editUserUrl.replace("<userId>", userId.toString()),
            {
              first_name: firstName,
              last_name: lastName
            }
          );
          addBanner({
            type: BannerType.Success,
            message: `User ${response.email} updated successfully.`
          });
          const users = useUserStore.getState().users;
          const updatedUsers = users.map(user => (user.id === userId ? response : user));
          set({ users: updatedUsers, isLoading: false });
        } catch (error) {
          console.error("Error updating user", error);
          set({ isLoading: false });
        }
      };

      const deleteUser = async (getAccessTokenSilently: GetAccessTokenFn, user: User) => {
        try {
          const urlToUse = user.user_type === "user" ? deleteUserUrl : deleteUserInviteUrl;
          await deleteRequest(
            getAccessTokenSilently,
            urlToUse.replace("<userId>", user.id.toString())
          );
          addBanner({
            type: BannerType.Success,
            message: `User ${user.email} deleted successfully.`
          });
          const users = useUserStore.getState().users;
          const updatedUsers = users.filter(u => u.id !== user.id);
          set({ users: updatedUsers, isLoading: false });
        } catch (error) {
          console.error("Error deleting user", error);
          set({ isLoading: false });
        }
      };

      const resendInvite = async (getAccessTokenSilently: GetAccessTokenFn, userId: string) => {
        try {
          await postRequest(
            getAccessTokenSilently,
            resendInviteUrl.replace("<userId>", userId),
            {}
          );
          addBanner({
            type: BannerType.Success,
            message: "Invite resent successfully."
          });
          set({ isLoading: false });
        } catch (error) {
          console.error("Error inviting user", error);
        }
      };

      const acceptInvite = async (
        team_invite_id: string,
        firstName: string,
        lastName: string,
        token: string
      ) => {
        try {
          await postRequestNoAuth(acceptInviteUrl.replace("<teamInviteId>", team_invite_id), {
            first_name: firstName,
            last_name: lastName,
            token
          });
          set({ isLoading: false });
          return true;
        } catch (error) {
          console.error("Error accepting invite", error);
          set({ isLoading: false });
          return false;
        }
      };

      return {
        isLoading: false,
        users: [],
        getUsers,
        requestSignup,
        getCurrentUser,
        resetCurrentUser,
        inviteNewUser,
        editUser,
        deleteUser,
        resendInvite,
        acceptInvite
      };
    },
    {
      name: "user-store",
      partialize: state => ({
        currentUser: state.currentUser
      })
    }
  )
);
